import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { formatNumber } from '@/utils/number';
import { DONATION_DECISION } from '../constants/marketPromotion.constants';
import { useAmendDonation, useDecideDonation, useRecommendDonation } from '../hooks/useMpMutations';
import type { MpApprovalRow } from '../types/marketPromotion.types';

export type DonationAction = 'recommend' | 'decide' | 'amend';

interface DonationFormDialogProps {
  donation: MpApprovalRow | null;
  action: DonationAction;
  onOpenChange: (open: boolean) => void;
}

interface DonationForm {
  approvedAmount: string;
  approvalComments: string;
  recommendedComments: string;
  paymentType: string;
  paymentPlace: string;
  transactionRef: string;
  witness: string;
  givenDate: string;
}

const TITLES: Record<DonationAction, string> = {
  recommend: 'Recommend',
  decide: 'Approve or reject',
  amend: 'Correct this donation',
};

const blank = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value.trim();

function toForm(donation: MpApprovalRow): DonationForm {
  return {
    approvedAmount: String(donation.approvedAmount || donation.claimedAmount),
    approvalComments: donation.approvalComments ?? '',
    recommendedComments: donation.recommendedComments ?? '',
    paymentType: donation.paymentType ?? '',
    paymentPlace: donation.paymentPlace ?? '',
    transactionRef: donation.transactionRef ?? '',
    witness: donation.witness ?? '',
    givenDate: donation.givenDate?.slice(0, 10) ?? donation.dcrDate.slice(0, 10),
  };
}

export function DonationFormDialog({ donation, action, onOpenChange }: DonationFormDialogProps) {
  return (
    <Dialog open={donation !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-lg">
        {donation === null ? null : (
          <DonationForm
            key={`${donation.detailId}-${action}`}
            donation={donation}
            action={action}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DonationForm({
  donation,
  action,
  onDone,
}: {
  donation: MpApprovalRow;
  action: DonationAction;
  onDone: () => void;
}) {
  const [form, setForm] = useState<DonationForm>(() => toForm(donation));
  const [error, setError] = useState<string | null>(null);

  const recommend = useRecommendDonation(onDone);
  const decide = useDecideDonation(onDone);
  const amend = useAmendDonation(onDone);

  const isPending = recommend.isPending || decide.isPending || amend.isPending;

  function payload() {
    return {
      approvedAmount: Number(form.approvedAmount),
      ...(blank(form.approvalComments) === undefined
        ? {}
        : { approvalComments: form.approvalComments.trim() }),
      ...(blank(form.recommendedComments) === undefined
        ? {}
        : { recommendedComments: form.recommendedComments.trim() }),
      ...(blank(form.paymentType) === undefined ? {} : { paymentType: form.paymentType.trim() }),
      ...(blank(form.paymentPlace) === undefined ? {} : { paymentPlace: form.paymentPlace.trim() }),
      ...(blank(form.transactionRef) === undefined
        ? {}
        : { transactionRef: form.transactionRef.trim() }),
      ...(blank(form.witness) === undefined ? {} : { witness: form.witness.trim() }),
      ...(blank(form.givenDate) === undefined ? {} : { givenDate: form.givenDate }),
    };
  }

  function valid(): boolean {
    const amount = Number(form.approvedAmount);
    if (Number.isNaN(amount) || amount < 0) {
      setError('Enter the amount in taka');
      return false;
    }
    return true;
  }

  function submit(approveTypeNo?: number) {
    if (!valid()) return;

    if (action === 'recommend') {
      recommend.mutate({ detailId: donation.detailId, payload: payload() });
      return;
    }

    if (action === 'amend') {
      amend.mutate({ detailId: donation.detailId, payload: payload() });
      return;
    }

    decide.mutate({
      detailId: donation.detailId,
      payload: {
        ...payload(),
        approveTypeNo: approveTypeNo ?? DONATION_DECISION.APPROVED,
        ...(approveTypeNo === DONATION_DECISION.REJECTED ? { approvedAmount: 0 } : {}),
      },
    });
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{TITLES[action]}</DialogTitle>
        <DialogDescription>
          {donation.itemName} · {donation.instituteName ?? 'no institute'} · claimed{' '}
          {formatNumber(donation.claimedAmount)} Tk by {donation.enteredByName ?? 'unknown'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">
            {action === 'recommend' ? 'Recommended Amount (Tk)' : 'Approved Amount (Tk)'}
          </span>
          <Input
            value={form.approvedAmount}
            onChange={(event) => {
              setForm({ ...form, approvedAmount: event.target.value });
              setError(null);
            }}
            inputMode="decimal"
            autoComplete="off"
          />
          {error === null ? null : <span className="text-xs text-destructive">{error}</span>}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">Payment Type</span>
            <Input
              value={form.paymentType}
              onChange={(event) => setForm({ ...form, paymentType: event.target.value })}
              placeholder="Cheque"
              autoComplete="off"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">Payment Place</span>
            <Input
              value={form.paymentPlace}
              onChange={(event) => setForm({ ...form, paymentPlace: event.target.value })}
              placeholder="At the institute"
              autoComplete="off"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">Transaction Ref</span>
            <Input
              value={form.transactionRef}
              onChange={(event) => setForm({ ...form, transactionRef: event.target.value })}
              placeholder="TRX-99182"
              autoComplete="off"
              className="tabular-nums"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium">Given Date</span>
            <DatePickerInput
              value={form.givenDate}
              onChange={(givenDate) => setForm({ ...form, givenDate })}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">Witness</span>
          <Input
            value={form.witness}
            onChange={(event) => setForm({ ...form, witness: event.target.value })}
            placeholder="Who saw the handover"
            autoComplete="off"
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium">
            {action === 'recommend' ? 'Recommendation Comment' : 'Approval Comment'}
          </span>
          <Input
            value={action === 'recommend' ? form.recommendedComments : form.approvalComments}
            onChange={(event) =>
              setForm(
                action === 'recommend'
                  ? { ...form, recommendedComments: event.target.value }
                  : { ...form, approvalComments: event.target.value },
              )
            }
            placeholder="Receipt seen"
            autoComplete="off"
          />
        </label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>

        {action === 'decide' ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => submit(DONATION_DECISION.REJECTED)}
            disabled={isPending}
          >
            Reject
          </Button>
        ) : null}

        <Button type="button" onClick={() => submit()} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          {action === 'recommend' ? 'Recommend' : action === 'amend' ? 'Save' : 'Approve'}
        </Button>
      </DialogFooter>
    </>
  );
}
